import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import cartService, { apiErrorMessage, CartItem, CartDetails } from '../../services/cartService';
import {
  getDisplayMrp,
  getDisplayPrice,
  getEffectiveDisplayPrice,
  getStrikeThroughPrice,
  needsOrderOptions,
  roundDisplayPrice,
} from '../../utils/menuPricing';
import {
  getInitialSelections,
  MenuOrderSelections,
  validateOrderSelections,
} from '../common/MenuOrderOptions/MenuOrderOptions';
import MenuItemOptionsSheet from '../common/MenuItemOptionsSheet/MenuItemOptionsSheet';
import { NOTE_HELPER_TEXT } from '../common/MenuOrderOptions/MenuOrderOptions';
import {
  cartLineKey,
  cartLineReactKey,
  cartOptionsFor,
  cartOptionsForLine,
  findCartLine,
  selectionLineKey,
  selectionsFromCartLine,
} from '../../utils/cartLines';
import { normalizeNote, noteLength, pruneSelections } from '../../utils/menuOptions';
import { noteSettingsFrom } from '../../hooks/useGuestNoteSettings';
import MenuRecommendationsStrip from '../common/MenuRecommendationsStrip/MenuRecommendationsStrip';
import restaurantService, {
  CustomerDisplaySettings,
  DineInTableInfo,
  DineInTableOption,
  formatAssignedSpaceLabel,
  formatDineInTableLabel,
  formatSpaceOptionLabel,
  MenuItem,
  MenuItemDetail,
  MenuRecommendationsPayload,
} from '../../services/restaurantService';
import { guestCodAvailable, guestPayOnlineAvailable } from '../../utils/guestPaymentMethods';
import {
  promptCancelOpenPayment,
  startOrResumeGuestOrderPayment,
} from '../../utils/paymentCheckout';

function setKioskPaymentBridgeActive(active: boolean): void {
  try {
    const bridge = window.PepparKiosk as { notifyPaymentActive?: (value: boolean) => void } | undefined;
    bridge?.notifyPaymentActive?.(active);
  } catch {
    /* native shell optional */
  }
}
import type { GuestOnlinePayments } from '../../utils/guestPaymentMethods';
import { DEFAULT_IMAGES, getMenuItemImageUrl } from '../../constants/images';
import {
  IconCart,
  IconCheck,
  IconLock,
} from '../RestaurantMenu/MenuListingIcons';
import { IconBag, IconBike, IconGift, IconTag, IconUtensils, IconChevronRight } from '../RestaurantHome/RestaurantDetailIcons';
import OffersDrawer from '../common/OffersDrawer/OffersDrawer';
import CouponCelebration from '../common/CouponCelebration/CouponCelebration';
import {
  couponMatchesOrderType,
  formatCouponBadge,
  formatCouponText,
} from '../../utils/offerFormatting';
import { useAppSelector } from '../../store/hooks';
import deliveryService, { DeliveryAddress, DeliveryQuote, formatDeliveryAddressLine } from '../../services/deliveryService';
import DeliveryAddressBook from '../DeliveryAddressBook/DeliveryAddressBook';
import Header from '../Header/Header';
import PageContainer from '../PageContainer/PageContainer';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import { getRestaurantBreadcrumbLabel, restaurantHomePath } from '../../utils/restaurantBreadcrumb';
import CartSkeleton from './CartSkeleton';
import { notifyOrderPlaced } from '../../utils/ongoingOrderContext';
import {
  CUSTOMER_FEATURE_DINE_IN,
  CUSTOMER_FEATURE_DIRECT_DELIVERY,
  CUSTOMER_FEATURE_TAKEAWAY,
  hasCustomerFeature,
  isGuestDeliveryOpen,
  PlanFeaturesPayload,
} from '../../utils/planFeatures';
import {
  getStoredGuestOrderType,
  parseGuestOrderType,
  resolveGuestOrderType,
  saveGuestOrderType,
  withGuestOrderTypeQuery,
} from '../../utils/guestOrderTypeStorage';
import { guestOrderingMessage, GuestOrderingStatus, isAcceptingGuestOrders } from '../../utils/guestOrdering';
import { LEGAL } from '../../config/legal';
import { SITE_CONFIG } from '../../config/siteConfig';
import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import LoginModal from '../LoginModal/LoginModal';
import Toast from '../Toast/Toast';
import { useKioskMode } from '../../contexts/KioskModeContext';
import { resolveKioskGuestOrderType } from '../Kiosk/kioskGuestOrderType';
import {
  kioskCartPath,
  kioskConfirmationPath,
  kioskMenuPath,
  kioskOrdersPath,
  kioskMustPayOnlineAtCheckout,
  persistKioskQueueToken,
  persistKioskRestroId,
  resolveKioskPaymentRestroId,
  KIOSK_SESSION_STORAGE_KEY,
  KIOSK_TABLE_STORAGE_KEY,
} from '../Kiosk/kioskPaths';
import { hasPendingSocialAuthError } from '../../utils/socialAuth';
import '../shared/ordersPageShell.css';
import './Cart.css';

interface Restaurant {
  id: number;
  appName: string;
  restaurantName: string;
  currencySymbol?: string;
}

type PublicCoupon = {
  id: number;
  couponCode?: string;
  couponType?: string;
  discount?: number;
  minAmount?: number;
  description?: string;
  orderType?: string;
};

const Cart: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const kioskMode = useKioskMode();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartDetails, setCartDetails] = useState<CartDetails | null>(null);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [menuRecommendations, setMenuRecommendations] = useState<MenuRecommendationsPayload | null>(null);
  const [publicCoupons, setPublicCoupons] = useState<PublicCoupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planFeatures, setPlanFeatures] = useState<PlanFeaturesPayload | undefined>(undefined);
  const [planFeaturesLoaded, setPlanFeaturesLoaded] = useState(false);
  const [orderType, setOrderType] = useState<number>(() => getStoredGuestOrderType(appName) ?? 1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<CartItem | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('error');
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const placingRef = useRef(false);
  const [activePromotions, setActivePromotions] = useState<Array<{ id: number; name: string; description?: string; discountPreview?: string }>>([]);
  const [addingMenuId, setAddingMenuId] = useState<number | null>(null);
  const [optionsSheetItem, setOptionsSheetItem] = useState<MenuItem | MenuItemDetail | null>(null);
  /** Cart line being edited in the options sheet (null = adding a new line). */
  const [editingLine, setEditingLine] = useState<CartItem | null>(null);
  // Cart rows with a request in flight (their +/−/remove are disabled until it settles).
  const pendingLinesRef = React.useRef<Set<string>>(new Set());
  const [pendingLines, setPendingLines] = useState<Set<string>>(() => new Set());
  const [kitchenNote, setKitchenNote] = useState('');
  const [optionsSelections, setOptionsSelections] = useState<MenuOrderSelections>({});
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [dineInTable, setDineInTable] = useState<DineInTableInfo | null>(null);
  const [dineInTables, setDineInTables] = useState<DineInTableOption[]>([]);
  const [allowManualTableEntry, setAllowManualTableEntry] = useState(false);
  const [guestHasRooms, setGuestHasRooms] = useState(false);
  const [guestHasTables, setGuestHasTables] = useState(true);
  const [manualTableNumber, setManualTableNumber] = useState('');
  const [dineInTablesLoading, setDineInTablesLoading] = useState(false);
  const [tableRequestLoading, setTableRequestLoading] = useState(false);
  const [dineInMessage, setDineInMessage] = useState<string | null>(null);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [guestOrdering, setGuestOrdering] = useState<GuestOrderingStatus | null>(null);
  const [displaySettings, setDisplaySettings] = useState<CustomerDisplaySettings | null>(null);
  const noteSettings = useMemo(() => noteSettingsFrom(displaySettings), [displaySettings]);
  const isAuthenticated = useAppSelector((s) => s.user.isAuthenticated);
  const authReady = useAppSelector((s) => s.user.authReady);
  const user = useAppSelector((s) => s.user.user);
  const [showLoginModal, setShowLoginModal] = useState(() => hasPendingSocialAuthError());
  const loginPromptedRef = React.useRef(false);
  const initialLoadDone = React.useRef(false);
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [deliveryConfigReady, setDeliveryConfigReady] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [deliveryAddresses, setDeliveryAddresses] = useState<DeliveryAddress[]>([]);
  const [addressDrawerOpen, setAddressDrawerOpen] = useState(false);
  const [deliveryQuote, setDeliveryQuote] = useState<DeliveryQuote | null>(null);
  const [checkoutPayMethod, setCheckoutPayMethod] = useState<'ONLINE_PAYMENT' | 'CASH_ON_DELIVERY'>('CASH_ON_DELIVERY');
  const [onlinePayments, setOnlinePayments] = useState<GuestOnlinePayments | null>(null);
  const [deliveryMessage, setDeliveryMessage] = useState<string | null>(null);
  const [deliveryMapCenter, setDeliveryMapCenter] = useState<{ lat: number; lon: number } | undefined>();
  const [offersDrawerOpen, setOffersDrawerOpen] = useState(false);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponCelebration, setCouponCelebration] = useState<{ code: string; savings?: number } | null>(null);
  // Dine-in service charge is optional for the guest (CCPA guidelines, 4 Jul 2022).
  const [serviceChargeOptOut, setServiceChargeOptOut] = useState(false);

  const dineInEnabled = hasCustomerFeature(planFeatures, CUSTOMER_FEATURE_DINE_IN);
  const takeawayEnabled = hasCustomerFeature(planFeatures, CUSTOMER_FEATURE_TAKEAWAY);
  const deliveryFeatureEnabled =
    !kioskMode.enabled && hasCustomerFeature(planFeatures, CUSTOMER_FEATURE_DIRECT_DELIVERY);
  const payOnlineAvailable = guestPayOnlineAvailable(onlinePayments);
  const codAvailable = guestCodAvailable(onlinePayments);
  const checkoutNeedsPayMethod = orderType === 2 || orderType === 3;

  useEffect(() => {
    setCheckoutPayMethod((current) => {
      if (kioskMode.enabled && kioskMode.payFirst && payOnlineAvailable) {
        return 'ONLINE_PAYMENT';
      }
      if (current === 'ONLINE_PAYMENT' && payOnlineAvailable) {
        return current;
      }
      if (current === 'CASH_ON_DELIVERY' && codAvailable) {
        return current;
      }
      if (payOnlineAvailable) {
        return 'ONLINE_PAYMENT';
      }
      return 'CASH_ON_DELIVERY';
    });
  }, [codAvailable, kioskMode.enabled, kioskMode.payFirst, payOnlineAvailable]);

  const loadCart = useCallback(async (opts?: { silent?: boolean }) => {
    if (!appName) return;
    if (!opts?.silent) {
      setLoading(true);
    }
    setError(null);
    try {
      // The server prices the cart with the guest's choices; the UI only displays its figures.
      const response = await cartService.getCart(appName, orderType, {
        serviceChargeOptOut,
        quoteId: orderType === 2 ? deliveryQuote?.quoteId : undefined,
        addressId: orderType === 2 && selectedAddressId ? selectedAddressId : undefined,
      });
      if (response.success) {
        setCartItems(response.cartItems || []);
        setCartDetails(response.cartDetails);
        setRestaurant(response.restaurant);
        if (response.restaurant?.id != null) {
          persistKioskRestroId(response.restaurant.id);
        }
        if (response.guestOrdering) {
          setGuestOrdering(response.guestOrdering);
        }
        if (response.dineInTable) {
          setDineInTable(response.dineInTable);
        }
      } else {
        setError(response.message || 'Failed to load cart');
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'An error occurred while loading cart');
    } finally {
      setLoading(false);
    }
  }, [appName, orderType, serviceChargeOptOut, deliveryQuote?.quoteId, selectedAddressId]);

  // Order note survives a reload of the cart page until the order is placed.
  const kitchenNoteStorageKey = appName ? `peppar:kitchenNote:${appName}` : null;
  useEffect(() => {
    if (!kitchenNoteStorageKey || typeof window === 'undefined') return;
    try {
      setKitchenNote(window.sessionStorage.getItem(kitchenNoteStorageKey) || '');
    } catch {
      /* storage blocked */
    }
  }, [kitchenNoteStorageKey]);

  const updateKitchenNote = (value: string) => {
    setKitchenNote(value);
    if (!kitchenNoteStorageKey || typeof window === 'undefined') return;
    try {
      if (value) {
        window.sessionStorage.setItem(kitchenNoteStorageKey, value);
      } else {
        window.sessionStorage.removeItem(kitchenNoteStorageKey);
      }
    } catch {
      /* storage blocked */
    }
  };

  const loadDineInContext = useCallback(async () => {
    if (!appName) return;
    setDineInTablesLoading(true);
    setDineInMessage(null);
    try {
      const [statusRes, tablesRes] = await Promise.all([
        restaurantService.getDineInTableStatus(appName),
        restaurantService.getDineInTables(appName),
      ]);
      if (statusRes.success && statusRes.dineInTable) {
        setDineInTable(statusRes.dineInTable);
      } else if (statusRes.success && !statusRes.hasTable) {
        setDineInTable(null);
      }
      if (tablesRes.success) {
        setDineInTables((tablesRes.tables || []).filter((t) => t.canSelect));
        setAllowManualTableEntry(tablesRes.allowManualTableEntry === true);
        setGuestHasRooms(tablesRes.hasRooms === true);
        setGuestHasTables(tablesRes.hasTables !== false);
      }
    } catch {
      setDineInMessage('Could not load table information. Please try again.');
    } finally {
      setDineInTablesLoading(false);
    }
  }, [appName]);

  useEffect(() => {
    if (!appName || !planFeaturesLoaded || orderType !== 1 || !dineInEnabled) {
      setDineInMessage(null);
      return;
    }
    if (!isAuthenticated) {
      setDineInTable(null);
      return;
    }
    loadDineInContext();
  }, [appName, orderType, dineInEnabled, planFeaturesLoaded, loadDineInContext, isAuthenticated]);

  const openLoginModal = useCallback(() => {
    setShowLoginModal(true);
  }, []);

  useEffect(() => {
    if (!authReady || isAuthenticated || loginPromptedRef.current) {
      return;
    }
    loginPromptedRef.current = true;
    setShowLoginModal(true);
  }, [authReady, isAuthenticated]);

  const handleLoginSuccess = useCallback(() => {
    setShowLoginModal(false);
    void loadCart({ silent: true });
  }, [loadCart]);

  useEffect(() => {
    if (!appName || orderType !== 1 || !dineInEnabled || !dineInTable) {
      return;
    }
    const interval = window.setInterval(async () => {
      try {
        const statusRes = await restaurantService.getDineInTableStatus(appName);
        if (statusRes.success && statusRes.dineInTable) {
          setDineInTable(statusRes.dineInTable);
        } else if (statusRes.success && !statusRes.hasTable) {
          setDineInTable(null);
        }
      } catch {
        /* ignore poll errors */
      }
    }, 10000);
    return () => window.clearInterval(interval);
  }, [appName, orderType, dineInTable?.requestId, dineInTable?.allotted]);

  useEffect(() => {
    if (!tableModalOpen) return undefined;
    document.body.style.overflow = 'hidden';
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTableModalOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [tableModalOpen]);

  useEffect(() => {
    if (!addressDrawerOpen) return undefined;
    document.body.style.overflow = 'hidden';
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAddressDrawerOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [addressDrawerOpen]);

  useEffect(() => {
    document.body.classList.add('cart-page-active');
    return () => document.body.classList.remove('cart-page-active');
  }, []);

  useEffect(() => {
    const isFirst = !initialLoadDone.current;
    if (isFirst) {
      window.scrollTo(0, 0);
      initialLoadDone.current = true;
    }

    const errorParam = searchParams.get('error');
    if (errorParam) {
      setToastMessage(decodeURIComponent(errorParam));
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }

    loadCart(isFirst ? undefined : { silent: true });
  }, [loadCart, searchParams, setSearchParams]);

  useEffect(() => {
    if (!planFeaturesLoaded) {
      return;
    }
    if (kioskMode.enabled) {
      const next = resolveKioskGuestOrderType(
        kioskMode.role,
        kioskMode.orderTypes,
        getStoredGuestOrderType(appName)
      );
      setOrderType(next);
      if (appName) {
        saveGuestOrderType(appName, next);
      }
      return;
    }
    const stored = getStoredGuestOrderType(appName);
    if (stored === 2 && !deliveryConfigReady && !searchParams.get('type') && !searchParams.get('token')) {
      return;
    }
    const typeParam = searchParams.get('type');
    const tableQr = Boolean(searchParams.get('token'));
    const requested = tableQr ? 1 : parseGuestOrderType(typeParam);
    const deliveryConfig = deliveryEnabled
      ? { enabled: true, featureEnabled: true, paused: false }
      : { enabled: false, featureEnabled: deliveryFeatureEnabled, paused: false };
    const next = resolveGuestOrderType({
      appName,
      requested,
      tableQr,
      features: planFeatures,
      deliveryConfig,
    });
    setOrderType(next);
    if (appName && (tableQr || requested != null || stored == null || next === stored)) {
      saveGuestOrderType(appName, next);
    }
  }, [
    planFeaturesLoaded,
    planFeatures,
    searchParams,
    deliveryEnabled,
    deliveryFeatureEnabled,
    deliveryConfigReady,
    appName,
    kioskMode.enabled,
    kioskMode.role,
    kioskMode.orderTypes,
  ]);

  const selectOrderType = (next: number) => {
    setOrderType(next);
    if (appName) {
      saveGuestOrderType(appName, next);
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('type', String(next));
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    if (!appName) return;
    Promise.all([
      cartService.getActivePromotions(appName),
      restaurantService.getPublicCoupons(appName),
      restaurantService.getRestaurant(appName),
    ]).then(([promoRes, couponRes, restRes]) => {
      if (promoRes.success && promoRes.promotions) {
        setActivePromotions(promoRes.promotions);
      }
      if (couponRes.success) {
        setPublicCoupons(couponRes.coupons || []);
      }
      if (restRes.success) {
        setPlanFeatures(restRes.features);
        setGuestOrdering(restRes.guestOrdering ?? null);
        setDisplaySettings(restRes.displaySettings ?? null);
        setOnlinePayments(restRes.onlinePayments ?? null);
        if (restRes.restaurant?.id != null) {
          persistKioskRestroId(restRes.restaurant.id);
        }
        const addr = restRes.restaurant?.restaurantAddressDetails;
        if (addr?.lat != null && addr?.lng != null) {
          const lat = Number(addr.lat);
          const lon = Number(addr.lng);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            setDeliveryMapCenter({ lat, lon });
          }
        }
      }
      setPlanFeaturesLoaded(true);
    }).catch(() => {
      setPlanFeaturesLoaded(true);
    });
  }, [appName]);

  useEffect(() => {
    if (!planFeaturesLoaded) {
      return;
    }
    if (!appName || !deliveryFeatureEnabled) {
      setDeliveryConfigReady(true);
      return;
    }
    let cancelled = false;
    deliveryService.getConfig(appName).then((cfg) => {
      if (cancelled) {
        return;
      }
      setDeliveryEnabled(isGuestDeliveryOpen(planFeatures, cfg));
      setDeliveryConfigReady(true);
    }).catch(() => {
      if (!cancelled) {
        setDeliveryEnabled(false);
        setDeliveryConfigReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [appName, deliveryFeatureEnabled, planFeatures, planFeaturesLoaded]);

  useEffect(() => {
    if (!isAuthenticated || orderType !== 2 || !deliveryFeatureEnabled) {
      return;
    }
    let cancelled = false;
    deliveryService.listUserAddresses().then((res) => {
      if (cancelled) {
        return;
      }
      const list = res.addresses || [];
      setDeliveryAddresses(list);
      setSelectedAddressId((current) => {
        if (current && list.some((row) => row.id === current)) {
          return current;
        }
        const preferred = list.find((row) => row.isDefault) || list[0];
        return preferred?.id || null;
      });
    }).catch(() => {
      if (!cancelled) {
        setDeliveryAddresses([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, orderType, deliveryFeatureEnabled, addressDrawerOpen]);

  useEffect(() => {
    if (!appName || orderType !== 2 || !selectedAddressId || !isAuthenticated) {
      setDeliveryQuote(null);
      return;
    }
    setDeliveryMessage(null);
    setDeliveryQuote(null);
    deliveryService
      .serviceability(appName, selectedAddressId)
      .then((check) => {
        if (!check.serviceable) {
          setDeliveryMessage(check.reason || check.message || 'This address is not deliverable');
          return;
        }
        return deliveryService.quote(appName, selectedAddressId).then((q) => {
          if (q.success) {
            setDeliveryQuote(q);
            setDeliveryMessage(null);
          } else {
            setDeliveryMessage(q.message || 'This address is not deliverable');
          }
        });
      })
      .catch((err: unknown) => {
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string; reason?: string } } }).response?.data?.message
              || (err as { response?: { data?: { reason?: string } } }).response?.data?.reason
            : undefined;
        setDeliveryQuote(null);
        setDeliveryMessage(message || 'Could not check delivery for this address');
      });
  }, [appName, orderType, selectedAddressId, isAuthenticated, cartItems.length, cartDetails?.couponDiscount, cartDetails?.totalDiscountedPrice]);

  const cartMenuIds = useMemo(() => new Set(cartItems.map((i) => i.menuId)), [cartItems]);

  useEffect(() => {
    if (!appName) return;
    const anchorIds = cartItems.map((item) => item.menuId);
    restaurantService
      .getMenuRecommendations(appName, anchorIds, 4)
      .then((res) => {
        if (res.success && res.recommendations) {
          setMenuRecommendations(res.recommendations);
        }
      })
      .catch(() => {});
  }, [appName, cartItems]);

  const applicableCoupons = useMemo(
    () => publicCoupons.filter((coupon) => couponMatchesOrderType(coupon.orderType, orderType)),
    [publicCoupons, orderType]
  );

  const previewDeals = useMemo(() => {
    const coupons = applicableCoupons.map((c) => ({
      id: `coupon-${c.id}`,
      label: c.couponCode || 'Offer',
      description: c.description || (c.couponCode ? `Use code ${c.couponCode}` : ''),
      icon: 'tag' as const,
    }));
    const promos = activePromotions.map((p) => ({
      id: `promo-${p.id}`,
      label: p.name,
      description: p.discountPreview || p.description || '',
      icon: 'gift' as const,
    }));
    return [...coupons, ...promos];
  }, [applicableCoupons, activePromotions]);

  const totalItemCount =
    cartDetails?.totalItemCount ??
    cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Every amount below is the server engine's (GET /cart `pricing`), computed by the same code
  // that prices the order at placement -- what the guest sees is what they pay, to the paise.
  const pricing = cartDetails?.pricing;
  const deliveryFeeRupees = pricing?.deliveryCharges ?? 0;
  const packingFeeRupees = pricing?.packingCharges ?? 0;
  const cgstPercent = pricing?.cgstPercent ?? 0;
  const sgstPercent = pricing?.sgstPercent ?? 0;
  const cgstRupees = pricing?.cgstAmount ?? 0;
  const sgstRupees = pricing?.sgstAmount ?? 0;
  const serviceChargeRupees = pricing?.serviceCharge ?? 0;
  const roundOffRupees = pricing?.roundOff ?? 0;
  const checkoutTotal = pricing?.grandTotal ?? 0;
  const deliveryFeeIncluded = orderType === 2 && !!cartDetails?.deliveryQuoteApplied;

  const promotionInclusiveSubtotal = useMemo(() => {
    if (cartDetails?.promotionInclusiveSubtotal != null && cartDetails.promotionInclusiveSubtotal > 0) {
      return cartDetails.promotionInclusiveSubtotal;
    }
    return cartItems.reduce((sum, item) => sum + roundDisplayPrice(item.itemTotal ?? 0), 0);
  }, [cartDetails?.promotionInclusiveSubtotal, cartItems]);

  const promotionIncludedInLinePrices = (cartDetails?.offerDiscount ?? 0) > 0;
  // With the promotion already shown in line prices the subtotal is after offers; either way it
  // is the engine's exact figure, not a sum of rounded line prices.
  const subtotalShown = pricing
    ? promotionIncludedInLinePrices
      ? Math.max(0, pricing.subTotal - pricing.promotionDiscount)
      : pricing.subTotal
    : promotionInclusiveSubtotal;

  const totalSavings =
    (cartDetails?.offerDiscount ?? 0) + (cartDetails?.couponDiscount ?? 0);

  const hasBillableCart = cartDetails != null && (cartDetails.totalItemPrice ?? 0) > 0;

  const guestMenuListPath = useMemo(() => {
    if (!appName) return '/';
    if (kioskMode.enabled) {
      return withGuestOrderTypeQuery(kioskMenuPath(appName), appName, orderType);
    }
    return withGuestOrderTypeQuery(`/restaurants/${appName}/menu`, appName, orderType);
  }, [appName, kioskMode.enabled, orderType]);

  const guestMenuItemPath = useCallback(
    (menu: { itemUrl?: string; id: number }) => {
      if (!appName) return '/';
      const slug = menu.itemUrl || String(menu.id);
      if (kioskMode.enabled) {
        return withGuestOrderTypeQuery(kioskMenuPath(appName, slug), appName, orderType);
      }
      return `/restaurants/${appName}/menu/${slug}`;
    },
    [appName, kioskMode.enabled, orderType]
  );

  const resolveGuestRedirect = useCallback(
    (redirectTo: string, existingOrderId?: number | string) => {
      if (!kioskMode.enabled || !appName) return redirectTo;
      if (existingOrderId != null) {
        return withGuestOrderTypeQuery(kioskOrdersPath(appName, existingOrderId), appName, orderType);
      }
      if (redirectTo.includes('/orders/')) {
        const matched = redirectTo.match(/\/orders\/(\d+)/);
        if (matched?.[1]) {
          return withGuestOrderTypeQuery(kioskOrdersPath(appName, matched[1]), appName, orderType);
        }
        return kioskOrdersPath(appName);
      }
      if (redirectTo.includes('/cart')) {
        return withGuestOrderTypeQuery(kioskCartPath(appName), appName, orderType);
      }
      return guestMenuListPath;
    },
    [appName, guestMenuListPath, kioskMode.enabled, orderType]
  );

  const dineInTableReady =
    kioskMode.enabled ||
    orderType !== 1 ||
    !dineInEnabled ||
    (dineInTable != null && dineInTable.allotted);
  const deliveryReady =
    orderType !== 2 ||
    !deliveryFeatureEnabled ||
    (deliveryEnabled && isAuthenticated && !!selectedAddressId && !!deliveryQuote?.quoteId
      && (payOnlineAvailable || codAvailable));
  const takeawayPayReady =
    orderType !== 3 ||
    !takeawayEnabled ||
    payOnlineAvailable ||
    codAvailable;
  const canPlaceOrder =
    hasBillableCart &&
    dineInTableReady &&
    deliveryReady &&
    takeawayPayReady &&
    isAcceptingGuestOrders(guestOrdering);

  useEffect(() => {
    const checkoutBarVisible = cartItems.length > 0 && hasBillableCart;
    document.body.classList.toggle('cart-checkout-bar-visible', checkoutBarVisible);
    return () => document.body.classList.remove('cart-checkout-bar-visible');
  }, [cartItems.length, hasBillableCart]);

  const checkoutBlockedReason =
    guestOrderingMessage(guestOrdering) ||
    (!kioskMode.enabled && dineInEnabled && orderType === 1 && dineInTable && !dineInTable.allotted
      ? `Table ${formatDineInTableLabel(dineInTable)} is pending staff approval. Checkout will unlock once your table is confirmed.`
      : !kioskMode.enabled && dineInEnabled && orderType === 1 && !dineInTable
        ? 'Select a table for dine-in before checkout, or switch to Take Away.'
        : orderType === 2 && deliveryFeatureEnabled && !deliveryEnabled
          ? 'This restaurant is not taking delivery orders right now.'
          : orderType === 2 && deliveryFeatureEnabled && !isAuthenticated
          ? 'Sign in to place a delivery order.'
          : orderType === 2 && deliveryFeatureEnabled && !deliveryQuote?.quoteId
            ? deliveryMessage || 'Choose a deliverable address before checkout.'
            : orderType === 2 && deliveryFeatureEnabled && !payOnlineAvailable && !codAvailable
              ? 'This restaurant has not enabled a guest payment method yet.'
            : orderType === 3 && takeawayEnabled && !payOnlineAvailable && !codAvailable
              ? 'This restaurant has not enabled a guest payment method yet.'
            : null);

  const formatPrice = (price: number | undefined | null): string => {
    if (price === undefined || price === null || isNaN(Number(price))) {
      return '₹0';
    }
    const numericPrice = typeof price === 'number' ? price : parseFloat(String(price));
    if (isNaN(numericPrice)) {
      return '₹0';
    }
    let currencySymbol = '₹';
    if (restaurant?.currencySymbol) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = restaurant.currencySymbol;
      currencySymbol = tempDiv.textContent || tempDiv.innerText || '₹';
    }
    return `${currencySymbol}${roundDisplayPrice(numericPrice)}`;
  };

  /** Bill amounts: paise shown when present (GST, service charge), whole rupees otherwise. */
  const formatAmount = (price: number | undefined | null): string => {
    const numericPrice = Number(price ?? 0);
    if (!Number.isFinite(numericPrice)) {
      return formatPrice(0);
    }
    const whole = Math.abs(numericPrice - Math.round(numericPrice)) < 0.005;
    if (whole) {
      return formatPrice(numericPrice);
    }
    return formatPrice(0).replace(/0$/, '') + numericPrice.toFixed(2);
  };

  const handleApplyCoupon = async (code: string) => {
    if (!appName || !code.trim()) {
      return;
    }
    setApplyingCoupon(true);
    setCouponError(null);
    try {
      const response = await cartService.applyCoupon(appName, code.trim(), orderType, serviceChargeOptOut);
      if (response.success) {
        setOffersDrawerOpen(false);
        setCouponError(null);
        setCouponCelebration({
          code: (response.code || code).trim().toUpperCase(),
          savings: typeof response.discountAmount === 'number' ? response.discountAmount : undefined,
        });
        await loadCart({ silent: true });
      } else {
        setCouponError(response.message || 'Could not apply this coupon');
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setCouponError(message || 'Could not apply this coupon');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const closeCouponCelebration = useCallback(() => {
    setCouponCelebration(null);
  }, []);

  const handleRemoveCoupon = async () => {
    if (!appName) {
      return;
    }
    setApplyingCoupon(true);
    setCouponError(null);
    try {
      await cartService.removeCoupon(appName);
      setToastMessage('Coupon removed');
      setToastType('info');
      await loadCart({ silent: true });
    } catch {
      setCouponError('Could not remove coupon');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const getImageUrl = (imageName: string | undefined, menuId: number): string => {
    if (!restaurant?.id || !menuId || !imageName || imageName === 'default') {
      return DEFAULT_IMAGES.MENU_ITEM;
    }
    return getMenuItemImageUrl(restaurant.id, menuId, imageName);
  };

  /** Sets one cart line's quantity (0 removes it); the line is identified by its options. */
  const setLineQuantity = async (
    menuId: number,
    quantity: number,
    options: ReturnType<typeof cartOptionsForLine>
  ): Promise<boolean> => {
    if (!appName) return false;
    try {
      const response = await cartService.addToCart(appName, menuId, quantity, orderType, options);
      if (!response.success) {
        setToastMessage(response.message || 'Could not update your cart');
        setToastType('error');
        return false;
      }
      return true;
    } catch (err: unknown) {
      setToastMessage(apiErrorMessage(err, 'Could not update your cart'));
      setToastType('error');
      return false;
    }
  };

  /** Runs one request for a cart row; further taps on that row are ignored until it settles. */
  const runLineRequest = async (line: CartItem, task: () => Promise<void>) => {
    const key = cartLineReactKey(line);
    if (pendingLinesRef.current.has(key)) return;
    pendingLinesRef.current.add(key);
    setPendingLines(new Set(pendingLinesRef.current));
    try {
      await task();
    } finally {
      pendingLinesRef.current.delete(key);
      setPendingLines(new Set(pendingLinesRef.current));
    }
  };

  /**
   * Removes exactly this line. A signed-in line with an id is deleted by id; a guest line is
   * addressed by its stored identity (variant, option ids, note) with quantity 0.
   */
  const removeLine = async (line: CartItem): Promise<boolean> => {
    if (!appName) return false;
    if (isAuthenticated && typeof line.cartId === 'number') {
      try {
        const response = await cartService.deleteCartItem(appName, line.cartId, line.menuId);
        if (!response.success) {
          setToastMessage(response.message || 'Could not update your cart');
          setToastType('error');
          return false;
        }
        return true;
      } catch (err: unknown) {
        setToastMessage(apiErrorMessage(err, 'Could not update your cart'));
        setToastType('error');
        return false;
      }
    }
    return setLineQuantity(line.menuId, 0, cartOptionsForLine(line));
  };

  const handleQuantityChange = (cartItem: CartItem, newQuantity: number) => {
    if (newQuantity < 1 || !appName) return;
    void runLineRequest(cartItem, async () => {
      await setLineQuantity(cartItem.menuId, newQuantity, cartOptionsForLine(cartItem));
      await loadCart({ silent: true });
    });
  };

  const handleRemoveItemClick = (cartItem: CartItem) => {
    setItemToDelete(cartItem);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!appName || !itemToDelete) return;
    const line = itemToDelete;
    try {
      // Removes exactly this line (other customisations of the item stay).
      await runLineRequest(line, async () => {
        if (await removeLine(line)) {
          await loadCart({ silent: true });
        }
      });
    } finally {
      setItemToDelete(null);
    }
  };

  const openOptionsSheet = (item: MenuItem | MenuItemDetail, line: CartItem | null) => {
    setEditingLine(line);
    setOptionsSelections(
      line ? pruneSelections(item, selectionsFromCartLine(line)) : getInitialSelections(item)
    );
    setOptionsError(null);
    setOptionsSheetItem(item);
  };

  const closeOptionsSheet = useCallback(() => {
    setOptionsSheetItem(null);
    setEditingLine(null);
    setOptionsError(null);
  }, []);

  /** "Edit" on a cart row: reopen the sheet with the line's variant, add-ons and note. */
  const handleEditLine = async (line: CartItem) => {
    if (!appName || addingMenuId != null) return;
    setAddingMenuId(line.menuId);
    try {
      const res = await restaurantService.getMenuItemDetail(
        appName,
        line.menu.itemUrl || String(line.menuId)
      );
      if (res.success && res.menuItem) {
        openOptionsSheet(res.menuItem, line);
      } else {
        setToastMessage(res.message || 'Could not load this item');
        setToastType('error');
      }
    } catch (err: unknown) {
      setToastMessage(apiErrorMessage(err, 'Could not load this item'));
      setToastType('error');
    } finally {
      setAddingMenuId(null);
    }
  };

  const handleQuickAdd = async (item: MenuItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!appName || addingMenuId != null) return;

    if (needsOrderOptions(item)) {
      openOptionsSheet(item, null);
      return;
    }

    setAddingMenuId(item.id);
    try {
      const selections = getInitialSelections(item);
      const existing = findCartLine(cartItems, item.id, selections, item);
      const options = existing ? cartOptionsForLine(existing) : cartOptionsFor(selections, item);
      if (await setLineQuantity(item.id, (existing?.quantity ?? 0) + 1, options)) {
        setToastMessage(`${item.item} added to cart`);
        setToastType('success');
        await loadCart({ silent: true });
      }
    } finally {
      setAddingMenuId(null);
    }
  };

  const handleOptionsSheetConfirm = async (sheetQuantity: number) => {
    if (!optionsSheetItem || !appName || addingMenuId != null) return;
    const item = optionsSheetItem;
    const err = validateOrderSelections(item, optionsSelections, noteSettings.itemNoteMaxLength);
    if (err) {
      setOptionsError(err);
      return;
    }
    setOptionsError(null);
    setAddingMenuId(item.id);
    try {
      const qty = Math.max(1, sheetQuantity);
      const newKey = selectionLineKey(item.id, optionsSelections, item);
      const editing = editingLine;
      const sameLine = editing != null && cartLineKey(editing) === newKey;
      // Another line with the same options merges with this one (design D5).
      const other = cartItems.find((l) => cartLineKey(l) === newKey && l !== editing);
      const nextQty = sameLine ? qty : (other?.quantity ?? 0) + qty;
      // An existing line (the one edited, or the one merged into) keeps its stored identity.
      const target = sameLine ? editing : other;
      const options = target ? cartOptionsForLine(target) : cartOptionsFor(optionsSelections, item);
      const ok = await setLineQuantity(item.id, nextQty, options);
      if (!ok) {
        return;
      }
      if (editing && !sameLine) {
        // Edit = add the new line, then remove the old one.
        if (!(await removeLine(editing))) {
          // The new line is in; the old one could not be removed. Keep the error (no "updated")
          // and show the cart as it really is.
          setToastMessage(
            `Your new choice for ${item.item} was added, but the old one could not be removed. Please remove it below.`
          );
          setToastType('error');
          closeOptionsSheet();
          await loadCart({ silent: true });
          return;
        }
      }
      setToastMessage(editing ? `${item.item} updated` : `${item.item} added to cart`);
      setToastType('success');
      closeOptionsSheet();
      await loadCart({ silent: true });
    } finally {
      setAddingMenuId(null);
    }
  };

  const handleSelectTable = async (tableId: number) => {
    if (!appName || tableRequestLoading) return;
    if (dineInTable?.tableId === tableId) {
      setTableModalOpen(false);
      return;
    }
    setTableRequestLoading(true);
    setDineInMessage(null);
    try {
      const response = await restaurantService.createDineInTableRequest(appName, tableId);
      if (response.redirectToJoin && response.orderId) {
        setDineInMessage(
          response.message || 'This table has an active order. Please join using the PIN on the menu page.'
        );
        return;
      }
      if (response.success) {
        if (response.tableNumber != null) {
          setDineInTable({
            requestId: response.requestId ?? 0,
            tableId,
            tableNumber: response.tableNumber,
            allotted: response.allotted === true,
            active: true,
            spaceType: response.spaceType,
            spaceNoun: response.spaceNoun,
            displayLabel: response.displayLabel,
          });
        }
        await loadDineInContext();
        setTableModalOpen(false);
        if (response.allotted !== true) {
          const spaceLabel = response.displayLabel || formatAssignedSpaceLabel({
            displayLabel: response.displayLabel,
            spaceType: response.spaceType,
            tableNumber: response.tableNumber,
          });
          setToastMessage(`${spaceLabel} requested. Waiting for staff approval.`);
          setToastType('info');
        }
      } else {
        setDineInMessage(response.message || 'Could not request table');
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setDineInMessage(message || 'Could not request table');
    } finally {
      setTableRequestLoading(false);
    }
  };

  const guestSpacePhrase =
    guestHasRooms && guestHasTables ? 'table or room' : guestHasRooms ? 'room' : 'table';

  const handleConfirmManualTable = async () => {
    if (!appName || tableRequestLoading) return;
    const parsed = parseInt(manualTableNumber.trim(), 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setDineInMessage(`Enter a valid ${guestSpacePhrase} number`);
      return;
    }
    if (dineInTable?.tableNumber === parsed) {
      setTableModalOpen(false);
      return;
    }
    setTableRequestLoading(true);
    setDineInMessage(null);
    try {
      const response = await restaurantService.createDineInTableRequestByNumber(appName, parsed);
      if (response.redirectToJoin && response.orderId) {
        setDineInMessage(
          response.message ||
            'This table has an active order. Please join using the PIN on the menu page.'
        );
        return;
      }
      if (response.success) {
        if (response.tableNumber != null) {
          setDineInTable({
            requestId: response.requestId ?? 0,
            tableId: response.tableId ?? 0,
            tableNumber: response.tableNumber,
            allotted: response.allotted === true,
            active: true,
            spaceType: response.spaceType,
            spaceNoun: response.spaceNoun,
            displayLabel: response.displayLabel,
          });
        }
        await loadDineInContext();
        setTableModalOpen(false);
        setManualTableNumber('');
        if (response.allotted !== true) {
          const spaceLabel =
            response.displayLabel ||
            formatAssignedSpaceLabel({
              displayLabel: response.displayLabel,
              spaceType: response.spaceType,
              tableNumber: response.tableNumber,
            });
          setToastMessage(`${spaceLabel} requested. Waiting for staff approval.`);
          setToastType('info');
        }
      } else {
        setDineInMessage(response.message || `Could not request that ${guestSpacePhrase}`);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setDineInMessage(message || `Could not request that ${guestSpacePhrase}`);
    } finally {
      setTableRequestLoading(false);
    }
  };

  const handleProceedToCheckout = async () => {
    if (!appName || placingRef.current) return;
    if (!isAcceptingGuestOrders(guestOrdering)) {
      setToastMessage(
        guestOrderingMessage(guestOrdering) ||
          'This restaurant is not accepting customer orders right now.'
      );
      setToastType('error');
      return;
    }
    if (!isAuthenticated) {
      setToastMessage('Sign in to place your order.');
      setToastType('error');
      openLoginModal();
      return;
    }
    if (deliveryFeatureEnabled && orderType === 2) {
      if (!selectedAddressId || !deliveryQuote?.quoteId) {
        setAddressDrawerOpen(true);
        setToastMessage(deliveryMessage || 'Choose a deliverable address before checkout.');
        setToastType('error');
        return;
      }
    }
    if (!kioskMode.enabled && dineInEnabled && orderType === 1 && !dineInTableReady) {
      if (!dineInTable) {
        setTableModalOpen(true);
      }
      setDineInMessage(checkoutBlockedReason || 'Table booking is required for dine-in orders.');
      setToastMessage(checkoutBlockedReason || 'Please select and confirm a table before checkout.');
      setToastType('error');
      return;
    }
    if (checkoutNeedsPayMethod && !payOnlineAvailable && !codAvailable) {
      setToastMessage('This restaurant has not enabled a guest payment method yet.');
      setToastType('error');
      return;
    }
    placingRef.current = true;
    setIsPlacingOrder(true);
    setError(null);
    try {
      const payExtras: {
        quoteId?: string;
        addressId?: number;
        paymentMethod?: string;
        orderSource?: string;
        kioskSessionId?: string | number;
        kioskTableId?: number;
      } | undefined =
        orderType === 2 && deliveryQuote?.quoteId && selectedAddressId
          ? {
              quoteId: deliveryQuote.quoteId,
              addressId: selectedAddressId,
              paymentMethod: checkoutPayMethod,
            }
          : orderType === 3
            ? { paymentMethod: checkoutPayMethod }
            : undefined;
      const orderExtras = { ...(payExtras || {}) };
        if (kioskMode.enabled) {
          orderExtras.orderSource = 'KIOSK';
          if (orderType === 2 || orderType === 3) {
            orderExtras.paymentMethod = checkoutPayMethod;
          } else if (kioskMode.payFirst && payOnlineAvailable) {
            orderExtras.paymentMethod = 'ONLINE_PAYMENT';
          }
          const sessionId =
          kioskMode.sessionId ||
          (typeof sessionStorage !== 'undefined'
            ? sessionStorage.getItem(KIOSK_SESSION_STORAGE_KEY)
            : null);
        if (sessionId) {
          orderExtras.kioskSessionId = sessionId;
        }
        if (orderType === 1) {
          let tablePk = kioskMode.tableId;
          if (tablePk == null && typeof sessionStorage !== 'undefined') {
            const stored = sessionStorage.getItem(KIOSK_TABLE_STORAGE_KEY);
            if (stored) {
              const n = parseInt(stored, 10);
              if (Number.isFinite(n)) tablePk = n;
            }
          }
          if (tablePk != null) {
            orderExtras.kioskTableId = tablePk;
          }
        }
      }
      const orderNote = noteSettings.orderNoteEnabled ? normalizeNote(kitchenNote) : '';
      const response = await cartService.placeOrder(appName, orderType, {
        ...orderExtras,
        ...(orderNote ? { kitchenNote: orderNote } : {}),
        serviceChargeOptOut: pricing?.serviceChargeOptional ? serviceChargeOptOut : undefined,
        // The server checks a new order against the total shown (adding to a running order
        // re-prices the whole bill instead).
        expectedGrandTotal: pricing ? pricing.grandTotal : undefined,
      });
      if (response.success && response.orderId) {
        updateKitchenNote('');
        if (kioskMode.enabled) {
          const tableDineIn = kioskMode.role === 'TABLE_KIOSK' && orderType === 1;
          const payOnlineNow = kioskMustPayOnlineAtCheckout(
            orderType,
            checkoutPayMethod,
            kioskMode.payFirst,
            payOnlineAvailable
          );
          if (response.queueTokenLabel && !tableDineIn && !payOnlineNow) {
            persistKioskQueueToken(response.orderId, response.queueTokenLabel);
          }

          const goKioskTableOrderDetails = () => {
            notifyOrderPlaced(appName, response.orderId!, restaurant?.restaurantName);
            navigate(
              withGuestOrderTypeQuery(kioskOrdersPath(appName, response.orderId), appName, orderType),
              { replace: true }
            );
          };

          const goKioskConfirmation = () => {
            notifyOrderPlaced(appName, response.orderId!, restaurant?.restaurantName);
            navigate(
              `${kioskConfirmationPath(appName)}?orderId=${encodeURIComponent(String(response.orderId))}`,
              {
                replace: true,
                state: {
                  queueTokenLabel: response.queueTokenLabel,
                  token: response.queueTokenLabel,
                  orderId: response.orderId,
                },
              }
            );
          };

          const runKioskPayment = async (): Promise<'paid' | 'redirected'> => {
            const paymentRestroId = resolveKioskPaymentRestroId(restaurant?.id);
            if (paymentRestroId == null) {
              throw new Error('Restaurant context is missing');
            }
            const paymentReturnBase = `${window.location.origin}/kiosk/${encodeURIComponent(appName)}`;
            const paymentReturnUrl = tableDineIn
              ? `${paymentReturnBase}/orders/${response.orderId}`
              : `${paymentReturnBase}/confirmation?orderId=${response.orderId}`;
            const paymentOpts = {
              restroId: paymentRestroId,
              orderId: response.orderId!,
              restaurantName:
                restaurant?.restaurantName || kioskMode.restaurantName || appName || 'Restaurant',
              orderCode: response.orderId,
              returnUrl: paymentReturnUrl,
            };
            try {
              setKioskPaymentBridgeActive(true);
              const result = await startOrResumeGuestOrderPayment(paymentOpts);
              if ('redirected' in result) {
                return 'redirected';
              }
              return 'paid';
            } finally {
              setKioskPaymentBridgeActive(false);
            }
          };

          if (payOnlineNow) {
            notifyOrderPlaced(appName, response.orderId, restaurant?.restaurantName);
            try {
              const outcome = await runKioskPayment();
              if (outcome === 'redirected') {
                setToastMessage('Complete payment to send your order to the kitchen.');
                setToastType('info');
                return;
              }
              setToastMessage('Payment received. Thank you!');
              setToastType('success');
            } catch (err: unknown) {
              const message =
                (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
                  ?.message || (err as { message?: string })?.message || 'Payment failed';
              if (message !== 'Payment cancelled') {
                setToastMessage(message);
                setToastType('error');
              } else {
                setToastMessage('Complete payment to send your order to the kitchen.');
                setToastType('info');
              }
              navigate(
                withGuestOrderTypeQuery(kioskOrdersPath(appName, response.orderId), appName, orderType),
                { replace: true, state: { autoPayOnline: true } }
              );
              return;
            }
          } else {
            notifyOrderPlaced(appName, response.orderId, restaurant?.restaurantName);
          }

          if (tableDineIn) {
            goKioskTableOrderDetails();
            return;
          }

          goKioskConfirmation();
          return;
        }
        notifyOrderPlaced(appName, response.orderId, restaurant?.restaurantName);
        const orderPath = `/restaurants/${appName}/orders/${response.orderId}`;
        const payOnlineNow = checkoutNeedsPayMethod && checkoutPayMethod === 'ONLINE_PAYMENT';
        if (payOnlineNow) {
          if (restaurant?.id) {
            try {
              const result = await startOrResumeGuestOrderPayment({
                restroId: restaurant.id,
                orderId: response.orderId,
                restaurantName: restaurant.restaurantName,
                orderCode: response.orderId,
              });
              if ('redirected' in result) {
                return;
              }
              setToastMessage('Payment received. Thank you!');
              setToastType('success');
              navigate(orderPath);
              return;
            } catch (err: unknown) {
              const message =
                (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
                  ?.message || (err as { message?: string })?.message || 'Payment failed';
              if (message !== 'Payment cancelled') {
                setToastMessage(message);
                setToastType('error');
              } else {
                setToastMessage('Order placed. Complete payment to send it to the kitchen.');
                setToastType('info');
              }
              navigate(orderPath, {
                state: {
                  autoPayOnline:
                    message !== 'Payment cancelled' && message !== 'Payment failed',
                },
              });
              return;
            }
          }
          setToastMessage('Order placed. Complete payment to send it to the kitchen.');
          setToastType('info');
          navigate(orderPath);
          return;
        }
        setToastMessage(
          response.notice ||
            (orderType === 2
              ? 'Delivery order placed. Pay cash to the rider.'
              : orderType === 3
                ? 'Takeaway order placed. Pay at the restaurant when you pick up.'
                : 'Order placed! You can keep adding items from the menu.')
        );
        setToastType(response.notice ? 'info' : 'success');
        navigate(orderType === 1 ? `/restaurants/${appName}/menu` : orderPath);
      } else if (response.redirectTo) {
        if (response.existingOrderId) {
          setToastMessage(response.message || 'You already have an ongoing order');
          setToastType('error');
          setTimeout(() => {
            navigate(
              resolveGuestRedirect(
                response.redirectTo || `/restaurants/${appName}/orders/${response.existingOrderId}`,
                response.existingOrderId
              )
            );
          }, 2000);
        } else if (response.redirectTo.includes('/table/booking')) {
          if (kioskMode.enabled) {
            setToastMessage(
              response.message ||
                'Could not assign your table automatically. Please ask staff for help or switch to Take Away.'
            );
            setToastType('error');
          } else {
            setTableModalOpen(true);
            setDineInMessage(
              response.message ||
                'Table booking is required for dine-in orders. Please select a table or switch to Take Away.'
            );
            setToastMessage(
              response.message ||
                'Table booking is required for dine-in orders. Please select a table or switch to Take Away.'
            );
            setToastType('error');
            await loadDineInContext();
          }
        } else {
          navigate(resolveGuestRedirect(response.redirectTo, response.existingOrderId));
        }
      } else if (response.priceChanged) {
        // Nothing was placed: show the server's new figures and let the guest confirm again.
        setToastMessage(response.message || 'Your bill changed. Please review it and place the order again.');
        setToastType('error');
        await loadCart({ silent: true });
      } else {
        setToastMessage(response.message || 'Failed to place order');
        setToastType('error');
      }
    } catch (err: unknown) {
      const errorResponse =
        err && typeof err === 'object' && 'response' in err
          ? (err as {
              response?: {
                status?: number;
                data?: { message?: string; redirectTo?: string; existingOrderId?: number; code?: string };
              };
            }).response
          : undefined;
      if (errorResponse?.status === 401) {
        setToastMessage('Sign in to place your order.');
        setToastType('error');
        openLoginModal();
        return;
      }
      const errorData = errorResponse?.data;
      const placeMessage = errorData?.message || 'An error occurred while placing the order';
      setToastMessage(placeMessage);
      setToastType('error');
      if (appName && placeMessage.includes('payment for this order is in progress')) {
        try {
          const cancelled = await promptCancelOpenPayment(appName);
          if (cancelled) {
            setToastMessage('Payment cancelled. You can add the items again.');
            setToastType('success');
          }
        } catch {
          setToastMessage('Could not cancel the payment. Open the order and try again.');
        }
      }
      if (
        errorData?.code === 'VARIANT_UNAVAILABLE' ||
        errorData?.code === 'MODIFIER_INVALID' ||
        errorData?.code === 'NOTE_TOO_LONG'
      ) {
        // A line's variant or add-on went out of stock: stay in the cart with fresh prices.
        await loadCart({ silent: true });
      } else if (errorData?.redirectTo?.includes('/table/booking')) {
        if (kioskMode.enabled) {
          setToastMessage(
            errorData.message ||
              'Could not place your kiosk order. Please ask staff for help or switch to Take Away.'
          );
          setToastType('error');
        } else {
          setTableModalOpen(true);
          setDineInMessage(
            errorData.message ||
              'Table booking is required for dine-in orders. Please select a table or switch to Take Away.'
          );
          await loadDineInContext();
        }
      } else if (errorData?.redirectTo && !errorData.redirectTo.includes('/table/booking')) {
        const redirectDelay = errorData.existingOrderId ? 2000 : 0;
        setTimeout(() => {
          if (errorData.redirectTo) {
            navigate(resolveGuestRedirect(errorData.redirectTo, errorData.existingOrderId));
          }
        }, redirectDelay);
      }
    } finally {
      placingRef.current = false;
      setIsPlacingOrder(false);
    }
  };

  const renderRecommendationStrip = (
    title: string,
    items: MenuItem[],
    variant: 'compact' | 'featured',
    subtitle?: string
  ) => {
    if (!restaurant || !appName || !items?.length) return null;
    return (
      <MenuRecommendationsStrip
        title={title}
        subtitle={subtitle}
        items={items}
        appName={appName}
        restroId={restaurant.id}
        variant={variant}
        cartMenuIds={cartMenuIds}
        formatPrice={formatPrice}
        onNavigate={navigateToMenuItem}
        onAdd={handleQuickAdd}
        addingMenuId={addingMenuId}
        showImages={displaySettings?.showMenuItemImages === true}
      />
    );
  };

  const renderCartRecommendations = (phase: 'before-summary' | 'after-summary' | 'all') => {
    if (!menuRecommendations) return null;
    const rec = menuRecommendations;

    if (phase === 'before-summary') {
      const strip = renderRecommendationStrip(
        'Frequently Bought Together',
        rec.frequentlyBoughtTogether,
        'compact'
      );
      if (!strip) return null;
      return <div className="cart-rec-sections">{strip}</div>;
    }

    if (phase === 'after-summary') {
      return (
        <div className="cart-rec-sections">
          {renderRecommendationStrip(
            "Today's Special",
            rec.todaysSpecial,
            'featured',
            "Chef's exclusive picks · Limited availability"
          )}
          {renderRecommendationStrip(
            'Best Selling Items',
            rec.bestSelling,
            'featured',
            'Guest favourites this week'
          )}
        </div>
      );
    }

    return (
      <div className="cart-rec-sections">
        {renderRecommendationStrip('Frequently Bought Together', rec.frequentlyBoughtTogether, 'compact')}
        {renderRecommendationStrip(
          "Today's Special",
          rec.todaysSpecial,
          'featured',
          "Chef's exclusive picks · Limited availability"
        )}
        {renderRecommendationStrip(
          'Best Selling Items',
          rec.bestSelling,
          'featured',
          'Guest favourites this week'
        )}
      </div>
    );
  };

  const navigateToMenuItem = (item: MenuItem) => {
    navigate(guestMenuItemPath(item));
  };

  const openTablePicker = () => {
    if (kioskMode.enabled) return;
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    setDineInMessage(null);
    setTableModalOpen(true);
    loadDineInContext();
  };

  const renderDineInSection = () => {
    if (kioskMode.enabled) return null;
    if (orderType !== 1 || !dineInEnabled) return null;

    const tableDisplay = dineInTable ? formatDineInTableLabel(dineInTable) : '';
    const assignedNoun = dineInTable?.spaceType === 'ROOM' ? 'room' : 'table';
    const changeButton = (
      <button
        type="button"
        className="cart-dine-in-change"
        onClick={openTablePicker}
        aria-label={`Change ${assignedNoun}`}
      >
        Change
      </button>
    );

    if (dineInTable?.allotted) {
      return (
        <div className="cart-dine-in cart-dine-in--confirmed">
          <p className="cart-field-label">Your {assignedNoun}</p>
          <div className="cart-dine-in-assigned">
            <p className="cart-dine-in-status">
              <IconCheck size={14} className="cart-dine-in-status-icon" aria-hidden />
              Seated at {dineInTable ? formatAssignedSpaceLabel(dineInTable) : tableDisplay}
            </p>
            {changeButton}
          </div>
        </div>
      );
    }

    if (dineInTable && !dineInTable.allotted) {
      return (
        <div className="cart-dine-in cart-dine-in--pending">
          <p className="cart-field-label">Your {assignedNoun}</p>
          <div className="cart-dine-in-assigned">
            <p className="cart-dine-in-status">
              {formatAssignedSpaceLabel(dineInTable)} — pending staff approval
            </p>
            {changeButton}
          </div>
          <p className="cart-dine-in-hint">
            Checkout unlocks once reception confirms your {assignedNoun}. This page refreshes automatically.
          </p>
        </div>
      );
    }

    return (
      <div className="cart-dine-in cart-dine-in--select">
        <p className="cart-field-label">Dine-in table</p>
        <button
          type="button"
          className="cart-dine-in-trigger"
          onClick={openTablePicker}
        >
          <span className="cart-dine-in-trigger-label">Select your table</span>
          <span className="cart-dine-in-trigger-action">Choose</span>
        </button>
        <p className="cart-dine-in-hint">
          Tap to pick a table. Staff will confirm before checkout.
        </p>
        {dineInMessage && <p className="cart-dine-in-error">{dineInMessage}</p>}
      </div>
    );
  };

  const renderDeliverySection = () => {
    if (orderType !== 2 || !deliveryFeatureEnabled) return null;

    if (!isAuthenticated) {
      return (
        <div className="cart-delivery">
          <p className="cart-field-label">Delivery address</p>
          <p className="cart-dine-in-hint">Sign in to save an address and get a delivery fee.</p>
          <button
            type="button"
            className="cart-dine-in-trigger"
            onClick={openLoginModal}
          >
            <span className="cart-dine-in-trigger-label">Sign in to continue</span>
            <span className="cart-dine-in-trigger-action">Sign in</span>
          </button>
        </div>
      );
    }

    const selectedAddress = deliveryAddresses.find((row) => row.id === selectedAddressId);
    const triggerLabel = selectedAddress
      ? `${selectedAddress.addressType || 'Home'} · ${formatDeliveryAddressLine(selectedAddress)}`
      : 'Add or select a delivery address';

    return (
      <div className="cart-delivery">
        <p className="cart-field-label">Delivery address</p>
        <button
          type="button"
          className="cart-dine-in-trigger"
          onClick={() => setAddressDrawerOpen(true)}
        >
          <span className="cart-dine-in-trigger-label">{triggerLabel}</span>
          <span className="cart-dine-in-trigger-action">{selectedAddress ? 'Change' : 'Choose'}</span>
        </button>
        {deliveryMessage && (
          <div className="cart-delivery-alert" role="alert">
            <div className="cart-delivery-alert__copy">
              <strong>Can't deliver to this address</strong>
              <p>{deliveryMessage}</p>
            </div>
            <button type="button" className="cart-delivery-alert__action" onClick={() => setAddressDrawerOpen(true)}>
              Change address
            </button>
          </div>
        )}
        {deliveryQuote?.quoteId && (
          <p className="cart-dine-in-hint">
            Delivery fee {formatPrice(deliveryFeeRupees)}
            {packingFeeRupees > 0 ? ` · Packing ${formatPrice(packingFeeRupees)}` : ''}
            {sgstRupees + cgstRupees > 0 ? ` · Tax ${formatAmount(sgstRupees + cgstRupees)}` : ''}
            {deliveryQuote.distanceM != null ? ` · ${(deliveryQuote.distanceM / 1000).toFixed(1)} km` : ''}
          </p>
        )}
        {renderPaymentMethodSection()}
      </div>
    );
  };

  const renderPaymentMethodSection = () => {
    if (!checkoutNeedsPayMethod) return null;
    const cashLabel = orderType === 3 ? 'Cash at pickup' : 'Cash on delivery';
    return (
      <>
        <p className="cart-field-label">Pay</p>
        <div className="cart-order-type-row">
          {payOnlineAvailable && (
            <button
              type="button"
              className={`cart-order-type-btn${checkoutPayMethod === 'ONLINE_PAYMENT' ? ' active' : ''}`}
              onClick={() => setCheckoutPayMethod('ONLINE_PAYMENT')}
            >
              Pay online
            </button>
          )}
          {codAvailable && (
            <button
              type="button"
              className={`cart-order-type-btn${checkoutPayMethod === 'CASH_ON_DELIVERY' ? ' active' : ''}`}
              onClick={() => setCheckoutPayMethod('CASH_ON_DELIVERY')}
            >
              {cashLabel}
            </button>
          )}
        </div>
        {!payOnlineAvailable && !codAvailable && (
          <p className="cart-dine-in-hint">This restaurant has not enabled a guest payment method yet.</p>
        )}
      </>
    );
  };

  const renderAddressDrawer = () => {
    if (!addressDrawerOpen) return null;

    const drawer = (
      <div
        className="cart-address-drawer-overlay"
        onClick={() => setAddressDrawerOpen(false)}
        role="presentation"
      >
        <div
          className="cart-address-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cart-address-drawer-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="cart-address-drawer__handle" aria-hidden />
          <div className="cart-address-drawer__header">
            <h2 id="cart-address-drawer-title">Delivery address</h2>
            <button
              type="button"
              className="cart-address-drawer__close"
              onClick={() => setAddressDrawerOpen(false)}
              aria-label="Close addresses"
            >
              ×
            </button>
          </div>
          <div className="cart-address-drawer__body">
            <DeliveryAddressBook
              selectable
              selectedId={selectedAddressId}
              onSelect={setSelectedAddressId}
              onPick={() => setAddressDrawerOpen(false)}
              onListChange={setDeliveryAddresses}
              defaultName={[user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.userName}
              defaultPhone={user?.phoneNumber}
              defaultCenter={deliveryMapCenter}
            />
          </div>
        </div>
      </div>
    );

    if (typeof document === 'undefined') {
      return drawer;
    }
    return createPortal(drawer, document.body);
  };

  const renderTableModal = () => {
    if (kioskMode.enabled || !tableModalOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        setTableModalOpen(false);
      }
    };

    return (
      <div className="cart-table-modal-overlay" onClick={handleBackdropClick} role="presentation">
        <div
          className="cart-table-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cart-table-modal-title"
        >
          <button
            type="button"
            className="cart-table-modal-close"
            onClick={() => setTableModalOpen(false)}
            aria-label="Close table selection"
          >
            ✕
          </button>
          <h2 id="cart-table-modal-title" className="cart-table-modal-title">
            {dineInTable ? 'Change table or room' : 'Select a table'}
          </h2>
          <p className="cart-table-modal-hint">
            {dineInTable
              ? 'Pick a different table or room. Staff will confirm before checkout.'
              : 'Choose your table for dine-in. Staff will confirm before checkout.'}
          </p>
          {dineInMessage && <p className="cart-dine-in-error">{dineInMessage}</p>}
          {dineInTablesLoading ? (
            <p className="cart-dine-in-loading">Loading tables…</p>
          ) : (
            <>
              {dineInTables.length > 0 ? (
                <div className="cart-dine-in-table-grid cart-dine-in-table-grid--modal">
                  {dineInTables.map((table) => {
                    const isCurrent = dineInTable?.tableId === table.id;
                    return (
                      <button
                        key={table.id}
                        type="button"
                        className={`cart-dine-in-table-btn${isCurrent ? ' cart-dine-in-table-btn--current' : ''}`}
                        disabled={tableRequestLoading}
                        onClick={() => handleSelectTable(table.id)}
                      >
                        {formatSpaceOptionLabel(table)}
                        {isCurrent && <span className="cart-dine-in-table-seats">Current</span>}
                        {!isCurrent && table.totalSeats != null && table.totalSeats > 0 && (
                          <span className="cart-dine-in-table-seats">{table.totalSeats} seats</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="cart-dine-in-empty">
                  <p>
                    {allowManualTableEntry
                      ? `No ${guestSpacePhrase} is listed for guests. Enter the number below if you know it.`
                      : 'No tables are available right now. Please try again shortly or switch to Take Away.'}
                  </p>
                </div>
              )}
              {allowManualTableEntry && (
                <div className="cart-manual-table">
                  {dineInTables.length > 0 && (
                    <p className="cart-manual-table-divider">or enter {guestSpacePhrase} number</p>
                  )}
                  <div className="cart-manual-table-row">
                    <input
                      type="text"
                      inputMode="numeric"
                      className="cart-manual-table-input"
                      placeholder="e.g. 12"
                      value={manualTableNumber}
                      onChange={(e) => setManualTableNumber(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleConfirmManualTable();
                        }
                      }}
                      aria-label={`${guestSpacePhrase} number`}
                    />
                    <button
                      type="button"
                      className="cart-manual-table-submit"
                      disabled={tableRequestLoading || !manualTableNumber.trim()}
                      onClick={handleConfirmManualTable}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderOrderSummary = () => (
    <section className="cart-summary-section" aria-label="Order summary">
      <div className="cart-summary-card">
        <div className="cart-summary-head">
          <h2 className="cart-summary-title">Order Summary</h2>
        </div>

        <div className="cart-summary-body">
          {planFeaturesLoaded &&
            !kioskMode.enabled &&
            (dineInEnabled || takeawayEnabled || deliveryFeatureEnabled) && (
          <div>
            <p className="cart-field-label">Order type</p>
            <div className={`cart-order-type-row${[dineInEnabled, takeawayEnabled, deliveryFeatureEnabled && deliveryEnabled].filter(Boolean).length > 2 ? ' cart-order-type-row--three' : ''}`}>
              {dineInEnabled && (
              <button
                type="button"
                className={`cart-order-type-btn${orderType === 1 ? ' active' : ''}`}
                onClick={() => selectOrderType(1)}
                aria-label="Dine In"
              >
                <span className="cart-order-type-dot" aria-hidden="true" />
                <span className="cart-order-type-icon" aria-hidden="true">
                  <IconUtensils size={16} />
                </span>
                <span className="cart-order-type-label">Dine In</span>
              </button>
              )}
              {deliveryFeatureEnabled && deliveryEnabled && (
              <button
                type="button"
                className={`cart-order-type-btn${orderType === 2 ? ' active' : ''}`}
                onClick={() => selectOrderType(2)}
                aria-label="Delivery"
              >
                <span className="cart-order-type-dot" aria-hidden="true" />
                <span className="cart-order-type-icon" aria-hidden="true">
                  <IconBike size={16} />
                </span>
                <span className="cart-order-type-label">Delivery</span>
              </button>
              )}
              {takeawayEnabled && (
              <button
                type="button"
                className={`cart-order-type-btn${orderType === 3 ? ' active' : ''}`}
                onClick={() => selectOrderType(3)}
                aria-label="Take Away"
              >
                <span className="cart-order-type-dot" aria-hidden="true" />
                <span className="cart-order-type-icon" aria-hidden="true">
                  <IconBag size={16} />
                </span>
                <span className="cart-order-type-label">Take Away</span>
              </button>
              )}
            </div>
          </div>
          )}

          {renderDineInSection()}
          {renderDeliverySection()}
          {orderType === 3 && takeawayEnabled ? (
            <div className="cart-delivery">{renderPaymentMethodSection()}</div>
          ) : null}

          {(previewDeals.length > 0 || (cartDetails?.couponDiscount ?? 0) > 0) && (
            <div className="cart-offers-block">
              <div className="cart-offers-head">
                <p className="cart-field-label">Available Offers</p>
                <button type="button" className="cart-offers-view-all" onClick={() => setOffersDrawerOpen(true)}>
                  View all <IconChevronRight size={12} />
                </button>
              </div>
              {(cartDetails?.appliedCouponCode || (cartDetails?.couponDiscount ?? 0) > 0) && (
                <div className="cart-coupon-success">
                  <span>
                    {cartDetails?.appliedCouponCode
                      ? `Coupon ${cartDetails.appliedCouponCode} applied`
                      : 'Coupon applied'}
                    {(cartDetails?.couponDiscount ?? 0) > 0 ? ` — ${formatPrice(cartDetails?.couponDiscount)} off` : ''}
                  </span>
                  <button type="button" className="cart-coupon-remove" onClick={handleRemoveCoupon} aria-label="Remove coupon">
                    ×
                  </button>
                </div>
              )}
              <div className="cart-offer-chips">
                {previewDeals.slice(0, 2).map((offer) => (
                  <button
                    key={offer.id}
                    type="button"
                    className="cart-offer-chip"
                    onClick={() => setOffersDrawerOpen(true)}
                  >
                    <span className="cart-offer-icon" aria-hidden="true">
                      {offer.icon === 'tag' ? <IconTag size={16} /> : <IconGift size={16} />}
                    </span>
                    <span className="cart-offer-text">
                      <strong>{offer.label}</strong>
                      {offer.description ? ` – ${offer.description}` : ''}
                    </span>
                  </button>
                ))}
              </div>
              <button type="button" className="cart-offers-open-btn" onClick={() => setOffersDrawerOpen(true)}>
                {previewDeals.length > 2 ? `View all ${previewDeals.length} deals` : 'View deals & apply coupon'}
              </button>
            </div>
          )}

          {previewDeals.length === 0 && (cartDetails?.couponDiscount ?? 0) <= 0 && (
            <button type="button" className="cart-offers-open-btn cart-offers-open-btn--solo" onClick={() => setOffersDrawerOpen(true)}>
              Have a coupon code? Apply it here
            </button>
          )}

          <div className="cart-bill">
            <div className="cart-bill-row">
              <span>
                Subtotal ({totalItemCount} {totalItemCount === 1 ? 'item' : 'items'})
              </span>
              <span>{formatAmount(subtotalShown)}</span>
            </div>
            {cartDetails && (cartDetails.offerDiscount ?? 0) > 0 && !promotionIncludedInLinePrices && (
              <div className="cart-bill-row discount">
                <span>
                  {cartDetails.appliedPromotionName
                    ? `Promotion: ${cartDetails.appliedPromotionName}`
                    : 'Promotion discount'}
                </span>
                <span>-{formatAmount(cartDetails.offerDiscount)}</span>
              </div>
            )}
            {cartDetails && (cartDetails.couponDiscount ?? 0) > 0 && (
              <div className="cart-bill-row discount">
                <span>Coupon discount{pricing?.couponCode ? ` (${pricing.couponCode})` : ''}</span>
                <span>-{formatAmount(cartDetails.couponDiscount)}</span>
              </div>
            )}
            {pricing?.couponRejected && pricing.couponMessage && (
              <p className="cart-dine-in-hint" role="status">
                Code removed: {pricing.couponMessage}
              </p>
            )}
            {totalSavings > 0 && (
              <div className="cart-bill-row saving">
                <IconGift size={14} className="cart-bill-saving-icon" aria-hidden />
                <span>You&apos;re saving {formatAmount(totalSavings)} on this order!</span>
              </div>
            )}
            {deliveryFeeIncluded && (
              <>
                <div className="cart-bill-row">
                  <span>Delivery fee</span>
                  <span>{deliveryFeeRupees > 0 ? formatAmount(deliveryFeeRupees) : 'Free'}</span>
                </div>
                {packingFeeRupees > 0 && (
                  <div className="cart-bill-row">
                    <span>Packing</span>
                    <span>{formatAmount(packingFeeRupees)}</span>
                  </div>
                )}
              </>
            )}
            {pricing?.serviceChargeApplicable && !pricing.serviceChargeOptional && (
              <div className="cart-bill-row">
                <span>Service charge ({pricing.serviceChargePercent}%)</span>
                <span>{formatAmount(serviceChargeRupees)}</span>
              </div>
            )}
            {pricing?.serviceChargeApplicable && pricing.serviceChargeOptional && (
              <div className="cart-bill-row cart-bill-row--optional">
                <span>
                  {serviceChargeOptOut
                    ? 'Service charge removed'
                    : `Service charge (${pricing.serviceChargePercent}%, optional)`}
                  <button
                    type="button"
                    className="cart-bill-optional-toggle"
                    onClick={() => setServiceChargeOptOut((v) => !v)}
                    aria-label={serviceChargeOptOut ? 'Add the service charge back' : 'Remove the service charge'}
                  >
                    {serviceChargeOptOut ? 'Add back' : 'Remove'}
                  </button>
                </span>
                <span>{serviceChargeOptOut ? formatPrice(0) : formatAmount(serviceChargeRupees)}</span>
              </div>
            )}
            {sgstRupees > 0 && (
              <div className="cart-bill-row">
                <span>SGST ({sgstPercent}%)</span>
                <span>{formatAmount(sgstRupees)}</span>
              </div>
            )}
            {cgstRupees > 0 && (
              <div className="cart-bill-row">
                <span>CGST ({cgstPercent}%)</span>
                <span>{formatAmount(cgstRupees)}</span>
              </div>
            )}
            {Math.abs(roundOffRupees) >= 0.005 && (
              <div className="cart-bill-row">
                <span>Round off</span>
                <span>
                  {roundOffRupees < 0 ? '-' : ''}
                  {formatAmount(Math.abs(roundOffRupees))}
                </span>
              </div>
            )}
            <div className="cart-bill-total">
              <span className="cart-bill-total-label">Total Amount</span>
              <span className="cart-bill-total-val">{formatPrice(checkoutTotal)}</span>
            </div>
          </div>
        </div>

        {hasBillableCart && (
          <div className="cart-checkout-section">
            {checkoutBlockedReason && (
              <p className="cart-checkout-blocked" role="status">
                {checkoutBlockedReason}
              </p>
            )}
            <p className="cart-guest-consent">
              By placing this order you agree to Peppar&apos;s{' '}
              <a
                href={`${SITE_CONFIG.urls.site}/terms-and-conditions`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms
              </a>{' '}
              and{' '}
              <a
                href={`${SITE_CONFIG.urls.site}/privacy-policy`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>{' '}
              (v{LEGAL.documentVersion}). The restaurant is responsible for the food and this order.
            </p>
            <button
              type="button"
              className="cart-checkout-btn cart-checkout-desktop"
              onClick={handleProceedToCheckout}
              disabled={isPlacingOrder || !canPlaceOrder}
            >
              <IconCart size={18} />
              {isPlacingOrder
                ? checkoutNeedsPayMethod && checkoutPayMethod === 'ONLINE_PAYMENT'
                  ? 'Opening payment…'
                  : 'Placing Order…'
                : 'Proceed to Checkout'}
              {!isPlacingOrder && totalItemCount > 0 && (
                <span className="cart-checkout-count">{totalItemCount}</span>
              )}
            </button>
            <p className="cart-secure-note">
              <IconLock size={12} aria-hidden />
              Secure &amp; encrypted checkout
            </p>
          </div>
        )}
      </div>
    </section>
  );

  const breadcrumbItems = [
    {
      label: getRestaurantBreadcrumbLabel(restaurant?.restaurantName, appName),
      path: kioskMode.enabled ? kioskMenuPath(appName || '') : restaurantHomePath(appName),
    },
    {
      label: 'Menu',
      path: kioskMode.enabled ? kioskMenuPath(appName || '') : `/restaurants/${appName}/menu`,
    },
    { label: 'Cart', active: true },
  ];

  const loginModal = (
    <LoginModal
      isOpen={showLoginModal}
      onClose={() => setShowLoginModal(false)}
      title="Sign in to continue"
      message="Sign in to view your cart and place your order."
      onLoginSuccess={handleLoginSuccess}
    />
  );

  if (loading) {
    return (
      <div className="cart-page">
        {!kioskMode.enabled && <Header />}
        <PageContainer>
          <div className="orders-page-shell">
          {!kioskMode.enabled && <Breadcrumb items={breadcrumbItems} />}
          <div className="cart-page-wrap">
            <CartSkeleton itemCount={3} />
          </div>
          </div>
        </PageContainer>
        {loginModal}
      </div>
    );
  }

  if (error) {
    return (
      <div className="cart-page">
        {!kioskMode.enabled && <Header />}
        <PageContainer>
          <div className="orders-page-shell">
          {!kioskMode.enabled && <Breadcrumb items={breadcrumbItems} />}
          <div className="cart-page-wrap">
            <div className="cart-error-message">{error}</div>
          </div>
          </div>
        </PageContainer>
        {loginModal}
      </div>
    );
  }

  return (
    <div
      className={`cart-page${cartItems.length > 0 ? ' cart-page--has-items' : ''}${
        cartItems.length > 0 && hasBillableCart ? ' cart-checkout-bar-visible' : ''
      }`}
    >
      {!kioskMode.enabled && <Header />}
      <PageContainer>
        <div className="orders-page-shell">
        {!kioskMode.enabled && <Breadcrumb items={breadcrumbItems} />}

        {cartItems.length === 0 ? (
          <div className="cart-page-wrap">
            <div className="cart-empty-state">
              <div className="cart-empty-icon" aria-hidden="true">
                🛒
              </div>
              <h2 className="cart-empty-title">Your cart is empty</h2>
              <p className="cart-empty-sub">Looks like you haven&apos;t added anything yet.</p>
              <Link to={guestMenuListPath} className="cart-empty-btn">
                Browse Menu
              </Link>
            </div>
            {renderCartRecommendations('all')}
          </div>
        ) : (
          <div className="cart-page-wrap cart-page-wrap--has-items">
            <div className="cart-page-main">
              <div className="cart-section-head">
                <h1 className="cart-section-title">Your Cart</h1>
                <span className="cart-item-count-badge">
                  {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'}
                </span>
              </div>

              <div className="cart-card">
                {cartItems.map((item) => {
                  const pricingSource = {
                    price: item.menu.price,
                    mrp: item.menu.mrp ?? item.menu.price,
                    sellingPrice: item.menu.sellingPrice ?? item.menu.price,
                    sizePrices: undefined,
                  };
                  const baseUnitPrice =
                    item.baseUnitPrice ??
                    getDisplayPrice(pricingSource, {
                      sizeTypeId: item.sizeTypeId,
                    });
                  const unitPrice =
                    item.effectiveUnitPrice ??
                    item.menu.effectiveSellingPrice ??
                    getDisplayPrice(pricingSource, {
                      sizeTypeId: item.sizeTypeId,
                    });
                  const mrp = getDisplayMrp(pricingSource, item.sizeTypeId);
                  const listPrice =
                    mrp != null && mrp > baseUnitPrice ? mrp : baseUnitPrice;
                  // Compare what is shown: an allocation remainder (e.g. 533.99) is not a discount.
                  const hasDiscount = roundDisplayPrice(listPrice) > roundDisplayPrice(unitPrice);
                  const discountPct = hasDiscount
                    ? Math.round(((listPrice - unitPrice) / listPrice) * 100)
                    : 0;
                  const itemTotal =
                    item.itemTotal != null
                      ? roundDisplayPrice(item.itemTotal)
                      : unitPrice * item.quantity;

                  return (
                    <div
                      key={cartLineReactKey(item)}
                      className="cart-card-item"
                    >
                      <div className="cart-item-img-wrap">
                        <Link
                          to={guestMenuItemPath(item.menu)}
                          className="cart-item-img-link"
                        >
                          <span className="cart-veg-dot-wrap">
                            <img
                              className="cart-item-img"
                              src={getImageUrl(item.menu.imageBig, item.menu.id)}
                              alt={item.menu.item}
                              onError={(e) => {
                                e.currentTarget.src = DEFAULT_IMAGES.MENU_ITEM;
                              }}
                            />
                            <span className="cart-veg-dot" aria-hidden="true" />
                          </span>
                        </Link>
                      </div>

                      <div className="cart-item-body">
                        <div>
                          <h3 className="cart-item-name">
                            <Link to={guestMenuItemPath(item.menu)}>{item.menu.item}</Link>
                          </h3>
                          {(() => {
                            // Older payloads have neither field: fall back to the size name.
                            const legacyLine =
                              item.optionsSummary === undefined && item.variantName === undefined;
                            const summary =
                              item.optionsSummary?.trim() ||
                              item.variantName?.trim() ||
                              (legacyLine ? item.sizeTypeName : '') ||
                              '';
                            const note = normalizeNote(item.itemNote);
                            const canEdit = item.hasOptions === true || noteSettings.itemNotesEnabled;
                            const editLabel = item.hasOptions
                              ? 'Edit'
                              : note
                                ? 'Edit request'
                                : 'Add cooking request';
                            return (
                              <div className="cart-item-customizations">
                                {summary && <p className="cart-item-options">{summary}</p>}
                                {note && <p className="cart-item-note">“{note}”</p>}
                                {canEdit && (
                                  <button
                                    type="button"
                                    className="cart-item-edit-link"
                                    onClick={() => handleEditLine(item)}
                                    disabled={addingMenuId != null}
                                    aria-label={`${editLabel}: ${item.menu.item}`}
                                  >
                                    {editLabel}
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="cart-item-pricing">
                          <span className="cart-item-unit-price">
                            {hasDiscount && <s>{formatPrice(listPrice)}</s>}{' '}
                            {formatPrice(unitPrice)}
                          </span>
                          <span className="cart-item-line-total">{formatPrice(itemTotal)}</span>
                          {discountPct > 0 && (
                            <span className="cart-item-discount-badge">{discountPct}% off</span>
                          )}
                        </div>
                      </div>

                      <div className="cart-item-right">
                        <button
                          type="button"
                          className="cart-remove-btn"
                          onClick={() => handleRemoveItemClick(item)}
                          disabled={pendingLines.has(cartLineReactKey(item))}
                          title="Remove item"
                          aria-label="Remove item from cart"
                        >
                          ✕
                        </button>
                        <div className="cart-qty-control">
                          <button
                            type="button"
                            className="cart-qty-btn"
                            onClick={() => handleQuantityChange(item, item.quantity - 1)}
                            disabled={item.quantity <= 1 || pendingLines.has(cartLineReactKey(item))}
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <span className="cart-qty-val">{item.quantity}</span>
                          <button
                            type="button"
                            className="cart-qty-btn"
                            onClick={() => handleQuantityChange(item, item.quantity + 1)}
                            disabled={pendingLines.has(cartLineReactKey(item))}
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {noteSettings.orderNoteEnabled && (
                  <div className="cart-kitchen-note">
                    <label className="cart-kitchen-note__label" htmlFor="cart-kitchen-note">
                      Note for the kitchen
                    </label>
                    <textarea
                      id="cart-kitchen-note"
                      className="cart-kitchen-note__input"
                      placeholder="e.g. serve starters first, Jain food"
                      value={kitchenNote}
                      maxLength={noteSettings.orderNoteMaxLength}
                      rows={2}
                      onChange={(e) => updateKitchenNote(e.target.value)}
                      aria-describedby="cart-kitchen-note-help"
                    />
                    <div className="cart-kitchen-note__meta">
                      <span id="cart-kitchen-note-help">{NOTE_HELPER_TEXT}</span>
                      <span aria-live="polite">
                        {noteLength(kitchenNote)}/{noteSettings.orderNoteMaxLength}
                      </span>
                    </div>
                  </div>
                )}

                <div className="cart-card-footer">
                  <Link to={guestMenuListPath} className="cart-continue-btn">
                    ← Add more items
                  </Link>
                  <div className="cart-footer-mini">
                    <span>
                      {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'}
                    </span>
                    {' · '}
                    <strong>{formatPrice(checkoutTotal)}</strong>
                  </div>
                </div>
              </div>

              {renderCartRecommendations('before-summary')}
            </div>
            {renderOrderSummary()}
            <div className="cart-page-after-summary">
              {renderCartRecommendations('after-summary')}
            </div>
          </div>
        )}
        </div>
      </PageContainer>

      {cartItems.length > 0 && hasBillableCart && (
        <div className="cart-checkout-mobile-bar" role="region" aria-label="Cart checkout">
          <div className="cart-checkout-mobile-bar__info">
            <span className="cart-checkout-mobile-bar__count">
              {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'}
            </span>
            <span className="cart-checkout-mobile-bar__total">{formatPrice(checkoutTotal)}</span>
          </div>
          <button
            type="button"
            className="cart-checkout-mobile-bar__btn"
            onClick={handleProceedToCheckout}
            disabled={isPlacingOrder || !canPlaceOrder}
          >
            <IconCart size={16} />
            {isPlacingOrder
              ? checkoutNeedsPayMethod && checkoutPayMethod === 'ONLINE_PAYMENT'
                ? 'Paying…'
                : 'Placing…'
              : 'Checkout'}
            {!isPlacingOrder && totalItemCount > 0 && (
              <span className="cart-checkout-count">{totalItemCount}</span>
            )}
          </button>
        </div>
      )}

      {toastMessage && (
        <Toast message={toastMessage} type={toastType} onClose={() => setToastMessage(null)} />
      )}

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Remove Item"
        message="Are you sure you want to remove this item from your cart?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonClass="danger"
      />

      {renderAddressDrawer()}
      {renderTableModal()}
      <OffersDrawer
        open={offersDrawerOpen}
        onClose={() => {
          setOffersDrawerOpen(false);
          setCouponError(null);
        }}
        promotions={activePromotions}
        coupons={publicCoupons}
        orderType={orderType}
        appliedCouponCode={cartDetails?.appliedCouponCode}
        appliedPromotionId={cartDetails?.appliedPromotionId}
        applying={applyingCoupon}
        applyError={couponError}
        onApplyCoupon={handleApplyCoupon}
        onRemoveCoupon={handleRemoveCoupon}
        formatCouponBadge={(coupon) => formatCouponBadge(coupon, '₹')}
        formatCouponText={(coupon) => formatCouponText(coupon, formatPrice)}
      />
      <CouponCelebration
        open={Boolean(couponCelebration)}
        code={couponCelebration?.code}
        savingsLabel={
          couponCelebration?.savings != null && couponCelebration.savings > 0
            ? formatPrice(couponCelebration.savings)
            : cartDetails?.couponDiscount != null && cartDetails.couponDiscount > 0
              ? formatPrice(cartDetails.couponDiscount)
              : null
        }
        onClose={closeCouponCelebration}
      />

      {optionsSheetItem && (
        <MenuItemOptionsSheet
          isOpen={optionsSheetItem != null}
          item={optionsSheetItem}
          appName={appName}
          selections={optionsSelections}
          onChange={setOptionsSelections}
          onClose={closeOptionsSheet}
          onConfirm={handleOptionsSheetConfirm}
          formatPrice={formatPrice}
          error={optionsError}
          busy={addingMenuId != null}
          initialQuantity={editingLine?.quantity ?? 1}
          confirmLabel={editingLine ? 'Update item' : 'Add to cart'}
        />
      )}
      {loginModal}
    </div>
  );
};

export default Cart;
